import { useState } from 'react';

import { Button, FieldLabel, InputSwitch, showSnackbar, TabPage, TabView, TextArea, TextInput } from 'fluxo-ui';
import { PaletteIcon, SaveIcon, SettingsIcon, ShieldIcon } from 'fluxo-ui/icons';

import type { ComponentEvent } from 'fluxo-ui';

interface OrganizationData {
    id?: string;
    name: string;
    domain: string;
    website?: string;
    description?: string;
    logo?: string;
    branding: {
        primaryColor?: string;
        secondaryColor?: string;
        logoUrl?: string;
    };
    settings: {
        allowPublicSignup: boolean;
        requireEmailVerification: boolean;
        allowGuestExams: boolean;
        defaultExamDuration: number;
        autoApproveQuestions: boolean;
        enableAnalytics: boolean;
    };
    subscription: {
        plan: string;
        maxUsers: number;
        maxExams: number;
        expiresAt?: string;
    };
}

interface OrganizationSettingsProps {
    organization: OrganizationData;
    onSave?: (data: OrganizationData) => Promise<void>;
}

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({ organization, onSave }) => {
    const [formData, setFormData] = useState<OrganizationData>(organization);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const updateField = <K extends keyof OrganizationData>(field: K, value: OrganizationData[K]) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const updateBranding = <K extends keyof OrganizationData['branding']>(field: K, value: OrganizationData['branding'][K]) => {
        setFormData((prev) => ({
            ...prev,
            branding: { ...prev.branding, [field]: value },
        }));
    };

    const updateSettings = <K extends keyof OrganizationData['settings']>(field: K, value: OrganizationData['settings'][K]) => {
        setFormData((prev) => ({
            ...prev,
            settings: { ...prev.settings, [field]: value },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave?.(formData);
            showSnackbar('Organization settings saved', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to save settings', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Simple client-side simulation
        setUploadingLogo(true);
        setTimeout(() => {
            const fakeUrl = URL.createObjectURL(file);
            updateBranding('logoUrl', fakeUrl);
            setUploadingLogo(false);
            showSnackbar('Logo uploaded successfully', 'Success', { type: 'success' });
        }, 1000);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Organization Settings</h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage your organization profile and preferences</p>
                </div>
                <Button
                    label="Save Changes"
                    leftIcon={SaveIcon}
                    variant="primary"
                    onClick={handleSave}
                    isLoading={saving}
                    disabled={saving}
                />
            </div>

            <TabView className="border border-slate-200 dark:border-slate-700 rounded-lg">
                {/* General Tab */}
                <TabPage header="General" leftIcon={SettingsIcon}>
                    <div className="p-6 space-y-4">
                        <FieldLabel label="Organization Name" required>
                            <TextInput
                                value={formData.name}
                                onChange={(e: ComponentEvent<string>) => updateField('name', e.value)}
                                placeholder="Enter organization name"
                            />
                        </FieldLabel>

                        <FieldLabel label="Domain" required hint="Used for email-based auto-join">
                            <TextInput
                                value={formData.domain}
                                onChange={(e: ComponentEvent<string>) => updateField('domain', e.value)}
                                placeholder="example.com"
                            />
                        </FieldLabel>

                        <FieldLabel label="Website">
                            <TextInput
                                value={formData.website || ''}
                                onChange={(e: ComponentEvent<string>) => updateField('website', e.value)}
                                placeholder="https://example.com"
                            />
                        </FieldLabel>

                        <FieldLabel label="Description">
                            <TextArea
                                value={formData.description || ''}
                                onChange={(e: ComponentEvent<string>) => updateField('description', e.value)}
                                placeholder="Brief description of your organization"
                                rows={4}
                            />
                        </FieldLabel>

                        {/* Subscription Info */}
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Subscription</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                    <div className="text-sm text-slate-600 dark:text-slate-400">Plan</div>
                                    <div className="text-lg font-semibold text-slate-900 dark:text-white">{formData.subscription.plan}</div>
                                </div>
                                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                    <div className="text-sm text-slate-600 dark:text-slate-400">Max Users</div>
                                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                                        {formData.subscription.maxUsers}
                                    </div>
                                </div>
                                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                    <div className="text-sm text-slate-600 dark:text-slate-400">Max Exams</div>
                                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                                        {formData.subscription.maxExams}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabPage>

                {/* Branding Tab */}
                <TabPage header="Branding" leftIcon={PaletteIcon}>
                    <div className="p-6 space-y-6">
                        {/* Logo Upload */}
                        <div>
                            <FieldLabel label="Organization Logo">
                                <div className="flex items-center gap-4">
                                    {formData.branding.logoUrl && (
                                        <img
                                            src={formData.branding.logoUrl}
                                            alt="Organization Logo"
                                            className="w-20 h-20 object-contain border border-slate-200 dark:border-slate-700 rounded-lg"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                            id="logo-upload"
                                        />
                                        <label htmlFor="logo-upload">
                                            <Button
                                                label={uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                                                variant="default"
                                                layout="outlined"
                                                disabled={uploadingLogo}
                                                onClick={() => document.getElementById('logo-upload')?.click()}
                                            />
                                        </label>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Recommended: 200x200px, PNG or JPG</p>
                                    </div>
                                </div>
                            </FieldLabel>
                        </div>

                        {/* Color Scheme */}
                        <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Color Scheme</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldLabel label="Primary Color">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={formData.branding.primaryColor || '#3B82F6'}
                                            onChange={(e) => updateBranding('primaryColor', e.target.value)}
                                            className="w-12 h-12 border border-slate-300 dark:border-slate-600 rounded cursor-pointer"
                                        />
                                        <TextInput
                                            value={formData.branding.primaryColor || '#3B82F6'}
                                            onChange={(e: ComponentEvent<string>) => updateBranding('primaryColor', e.value)}
                                            placeholder="#3B82F6"
                                        />
                                    </div>
                                </FieldLabel>

                                <FieldLabel label="Secondary Color">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={formData.branding.secondaryColor || '#8B5CF6'}
                                            onChange={(e) => updateBranding('secondaryColor', e.target.value)}
                                            className="w-12 h-12 border border-slate-300 dark:border-slate-600 rounded cursor-pointer"
                                        />
                                        <TextInput
                                            value={formData.branding.secondaryColor || '#8B5CF6'}
                                            onChange={(e: ComponentEvent<string>) => updateBranding('secondaryColor', e.value)}
                                            placeholder="#8B5CF6"
                                        />
                                    </div>
                                </FieldLabel>
                            </div>
                        </div>

                        {/* Preview */}
                        <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Preview</h3>
                            <div className="p-6 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                                <div className="flex items-center gap-4 mb-4">
                                    {formData.branding.logoUrl && (
                                        <img src={formData.branding.logoUrl} alt="Logo Preview" className="w-12 h-12 object-contain" />
                                    )}
                                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{formData.name}</div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        className="px-4 py-2 rounded font-medium text-white"
                                        style={{ backgroundColor: formData.branding.primaryColor || '#3B82F6' }}
                                    >
                                        Primary Button
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded font-medium text-white"
                                        style={{ backgroundColor: formData.branding.secondaryColor || '#8B5CF6' }}
                                    >
                                        Secondary Button
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabPage>

                {/* Settings Tab */}
                <TabPage header="Preferences" leftIcon={ShieldIcon}>
                    <div className="p-6 space-y-4">
                        <div className="space-y-4">
                            <FieldLabel label="Allow public signup" hint="Let anyone with your domain email sign up automatically">
                                <InputSwitch
                                    checked={formData.settings.allowPublicSignup}
                                    onChange={(e: ComponentEvent<boolean>) => updateSettings('allowPublicSignup', e.value)}
                                />
                            </FieldLabel>

                            <FieldLabel
                                label="Require email verification"
                                hint="Users must verify their email before accessing the platform"
                            >
                                <InputSwitch
                                    checked={formData.settings.requireEmailVerification}
                                    onChange={(e: ComponentEvent<boolean>) => updateSettings('requireEmailVerification', e.value)}
                                />
                            </FieldLabel>

                            <FieldLabel
                                label="Allow guest exam attempts"
                                hint="Enable non-registered users to take exams without an account"
                            >
                                <InputSwitch
                                    checked={formData.settings.allowGuestExams}
                                    onChange={(e: ComponentEvent<boolean>) => updateSettings('allowGuestExams', e.value)}
                                />
                            </FieldLabel>

                            <FieldLabel label="Auto-approve questions" hint="Skip manual approval for questions created by instructors">
                                <InputSwitch
                                    checked={formData.settings.autoApproveQuestions}
                                    onChange={(e: ComponentEvent<boolean>) => updateSettings('autoApproveQuestions', e.value)}
                                />
                            </FieldLabel>

                            <FieldLabel label="Enable analytics" hint="Collect and analyze usage data for insights">
                                <InputSwitch
                                    checked={formData.settings.enableAnalytics}
                                    onChange={(e: ComponentEvent<boolean>) => updateSettings('enableAnalytics', e.value)}
                                />
                            </FieldLabel>
                        </div>

                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                            <FieldLabel label="Default Exam Duration (minutes)" hint="Default time limit for new exams">
                                <TextInput
                                    type="number"
                                    value={String(formData.settings.defaultExamDuration)}
                                    onChange={(e: ComponentEvent<string>) => updateSettings('defaultExamDuration', parseInt(e.value) || 60)}
                                />
                            </FieldLabel>
                        </div>
                    </div>
                </TabPage>
            </TabView>
        </div>
    );
};

export default OrganizationSettings;
